import React from 'react';
import { Link } from 'react-router-dom';

const PolitiqueConfidentialite = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Breadcrumb */}
        <nav className="flex mb-8" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-4">
            <li>
              <Link to="/" className="text-gray-400 hover:text-gray-500">
                Accueil
              </Link>
            </li>
            <li>
              <svg className="flex-shrink-0 h-5 w-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
              </svg>
            </li>
            <li>
              <span className="text-gray-700 font-medium">Politique de confidentialité</span>
            </li>
          </ol>
        </nav>

        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Politique de confidentialité</h1>
          <p className="text-gray-600">
            Protection et traitement de vos données personnelles chez ECOCLIMA
          </p>
          <div className="mt-4 p-3 bg-green-50 rounded-lg">
            <p className="text-sm text-green-800">
              <strong>Conforme RGPD</strong> - Cette politique respecte le Règlement Général sur la Protection des Données et la loi Informatique et Libertés.
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-lg p-8 space-y-8">
          
          {/* Article 1 - Préambule */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Préambule</h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 mb-4">
                ECOCLIMA s'engage à protéger votre vie privée et vos données personnelles. Cette politique de confidentialité vous informe sur nos pratiques concernant la collecte, l'utilisation et la protection de vos données personnelles.
              </p>
              <p className="text-gray-700">
                Cette politique est conforme au <strong>Règlement Général sur la Protection des Données (RGPD)</strong> et à la <strong>loi Informatique et Libertés</strong>.
              </p>
            </div>
          </section>

          {/* Article 2 - Responsable du traitement */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Responsable du traitement</h2>
            <div className="bg-blue-50 p-6 rounded-lg">
              <p className="text-gray-700 mb-4">
                Le responsable du traitement de vos données personnelles est :
              </p>
              <div className="space-y-2">
                <p className="font-semibold text-gray-900">ECOCLIMA SARL</p>
                <p className="text-gray-700">15 Lotissement Les Cannelles Convenance, 97122 Baie-Mahault</p>
                <p className="text-gray-700">Email : <a href="mailto:contact@ecoclima.energy" className="text-blue-600 hover:text-blue-800">contact@ecoclima.energy</a></p>
                <p className="text-gray-700">Téléphone : <a href="tel:+590590197070" className="text-blue-600 hover:text-blue-800">0590 19 70 70</a></p>
              </div>
            </div>
          </section>

          {/* Article 3 - Données collectées */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Données collectées</h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 mb-4">
                Nous collectons les catégories de données personnelles suivantes :
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Données d'identité</h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Nom et prénom</li>
                    <li>• Email</li>
                    <li>• Téléphone</li>
                  </ul>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Coordonnées</h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Adresse postale</li>
                    <li>• Code postal</li>
                    <li>• Ville</li>
                  </ul>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Informations commerciales</h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Devis demandés</li>
                    <li>• Commandes</li>
                    <li>• Préférences produits</li>
                  </ul>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Données techniques</h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Adresse IP</li>
                    <li>• Navigateur utilisé</li>
                    <li>• Pages visitées</li>
                  </ul>
                </div>
              </div>
              <div className="mt-4 p-3 bg-red-50 border-l-4 border-red-400">
                <p className="text-red-800 font-medium">
                  <strong>Important :</strong> Aucune donnée sensible (origine, santé, religion, etc.) n'est collectée par ECOCLIMA.
                </p>
              </div>
            </div>
          </section>

          {/* Article 4 - Finalités du traitement */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Finalités du traitement</h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 mb-4">
                Vos données personnelles sont collectées et traitées pour les finalités suivantes :
              </p>
              <div className="space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h3 className="font-semibold text-gray-900">Gestion commerciale</h3>
                  <p className="text-gray-700 text-sm">Traitement des devis, commandes, installations et service après-vente</p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h3 className="font-semibold text-gray-900">Service client</h3>
                  <p className="text-gray-700 text-sm">Réponses aux demandes d'information et support technique</p>
                </div>
                <div className="border-l-4 border-yellow-500 pl-4">
                  <h3 className="font-semibold text-gray-900">Gestion de la relation client</h3>
                  <p className="text-gray-700 text-sm">Suivi de la satisfaction et amélioration de nos services</p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="font-semibold text-gray-900">Communication marketing</h3>
                  <p className="text-gray-700 text-sm">Uniquement avec votre consentement préalable</p>
                </div>
              </div>
            </div>
          </section>

          {/* Article 5 - Destinataires des données */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Destinataires des données</h2>
            <div className="prose prose-gray max-w-none">
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-4">
                  Vos données personnelles sont destinées aux :
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                  <li><strong>Services internes d'ECOCLIMA</strong> (commercial, technique, administratif)</li>
                  <li><strong>Prestataires techniques et logistiques</strong> (livraison, installation)</li>
                </ul>
                <div className="bg-green-100 p-4 rounded-lg">
                  <p className="text-green-800 font-medium">
                    🔒 <strong>Engagement :</strong> Vos données ne sont jamais revendues à des tiers.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Article 6 - Durée de conservation */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Durée de conservation</h2>
            <div className="bg-blue-50 p-6 rounded-lg">
              <div className="space-y-3">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-blue-500 rounded-full mr-3"></div>
                  <p className="text-gray-700">
                    <strong>Pendant la relation contractuelle :</strong> Conservation active de vos données
                  </p>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-yellow-500 rounded-full mr-3"></div>
                  <p className="text-gray-700">
                    <strong>Après la fin de la relation :</strong> Archivage pendant 5 ans
                  </p>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-red-500 rounded-full mr-3"></div>
                  <p className="text-gray-700">
                    <strong>Obligations légales :</strong> Conservation plus longue si requise par la loi
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Article 7 - Droits des utilisateurs */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Vos droits RGPD</h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 mb-6">
                Conformément au RGPD, vous disposez des droits suivants concernant vos données personnelles :
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-blue-900 mb-2">🔍 Droit d'accès</h3>
                  <p className="text-sm text-gray-700">Obtenir une copie de vos données personnelles</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-green-900 mb-2">✏️ Droit de rectification</h3>
                  <p className="text-sm text-gray-700">Corriger ou mettre à jour vos données</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-red-900 mb-2">🗑️ Droit à l'effacement</h3>
                  <p className="text-sm text-gray-700">Demander la suppression de vos données</p>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-yellow-900 mb-2">⏸️ Droit de limitation</h3>
                  <p className="text-sm text-gray-700">Limiter le traitement de vos données</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-purple-900 mb-2">🚫 Droit d'opposition</h3>
                  <p className="text-sm text-gray-700">Vous opposer au traitement</p>
                </div>
                <div className="bg-indigo-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-indigo-900 mb-2">📦 Droit à la portabilité</h3>
                  <p className="text-sm text-gray-700">Récupérer vos données dans un format lisible</p>
                </div>
              </div>
              
              <div className="bg-gray-100 p-6 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-3">Comment exercer vos droits ?</h3>
                <p className="text-gray-700 mb-3">
                  Pour exercer l'un de ces droits, contactez-nous par email :
                </p>
                <a 
                  href="mailto:contact@ecoclima.energy" 
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  contact@ecoclima.energy
                </a>
                <p className="text-sm text-gray-600 mt-3">
                  En cas de litige, vous pouvez saisir la <strong>CNIL</strong> (Commission Nationale de l'Informatique et des Libertés).
                </p>
              </div>
            </div>
          </section>

          {/* Article 8 - Sécurité */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Sécurité des données</h2>
            <div className="bg-green-50 p-6 rounded-lg">
              <p className="text-gray-700 mb-4">
                ECOCLIMA met en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données personnelles contre :
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>L'accès non autorisé</li>
                <li>La perte ou la destruction accidentelle</li>
                <li>L'utilisation malveillante</li>
                <li>La divulgation non autorisée</li>
              </ul>
            </div>
          </section>

          {/* Article 9 - Cookies et traceurs */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Cookies et traceurs</h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 mb-4">
                Notre site web peut utiliser des cookies et autres traceurs pour :
              </p>
              <div className="bg-yellow-50 p-4 rounded-lg mb-4">
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  <li>Mesurer l'audience et analyser la fréquentation</li>
                  <li>Améliorer l'expérience utilisateur</li>
                  <li>Personnaliser les contenus</li>
                </ul>
              </div>
              <p className="text-gray-700">
                Vous pouvez paramétrer vos préférences concernant les cookies directement dans votre navigateur web.
              </p>
            </div>
          </section>

          {/* Article 10 - Droit applicable */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Droit applicable</h2>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700 mb-2">
                Cette politique de confidentialité est régie par le <strong>droit français</strong>.
              </p>
              <p className="text-gray-700">
                Tout litige sera soumis à la compétence exclusive des <strong>tribunaux de Pointe-à-Pitre</strong>.
              </p>
            </div>
          </section>

        </div>
        
        {/* Navigation links */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <Link 
            to="/mentions-legales" 
            className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-center"
          >
            Mentions légales
          </Link>
          <Link 
            to="/cgv" 
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-center"
          >
            Conditions générales de vente
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PolitiqueConfidentialite;